// libraries
import React from 'react'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'

export interface EventDateProps {
	title?: string
	description?: string
	startDate?: string
	endDate?: string
	time?: string
	location?: string
}

const dayFormatter = new Intl.DateTimeFormat('nb-NO', { day: '2-digit', timeZone: 'UTC' })
const monthFormatter = new Intl.DateTimeFormat('nb-NO', { month: 'long', timeZone: 'UTC' })
const monthShortFormatter = new Intl.DateTimeFormat('nb-NO', { month: 'short', timeZone: 'UTC' })

const parseDate = (value?: string): Date | null => {
	if (!isNonWhitespaceString(value)) {
		return null
	}

	// If the format is YYYY-MM-DD, we parse it as UTC manually.
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim())
	if (match) {
		const [_, y, m, d] = match.map(Number)
		return new Date(Date.UTC(y, m - 1, d))
	}

	// Otherwise fallback to normal parsing (strings with time)
	const parsed = new Date(value as string)
	return Number.isNaN(parsed.getTime()) ? null : parsed
}

export default function EventDate({
	startDate,
	endDate,
	time,
	location
}: EventDateProps) {
	const parsedStartDate = parseDate(startDate)
	if (!parsedStartDate) {
		return null
	}

	const parsedEndDate = parseDate(endDate)
	const endDateValue = parsedEndDate || parsedStartDate
	const hasRange = parsedEndDate && endDateValue.getTime() !== parsedStartDate.getTime()

	const startDay = dayFormatter.format(parsedStartDate)
	const endDay = dayFormatter.format(endDateValue)
	const startMonthShort = monthShortFormatter.format(parsedStartDate)
	const endMonthShort = monthShortFormatter.format(endDateValue)
	const startMonthFull = monthFormatter.format(parsedStartDate)
	const endMonthFull = monthFormatter.format(endDateValue)
	const startDayNumber = Number(startDay)
	const endDayNumber = Number(endDay)
	const startYear = parsedStartDate.getFullYear()
	const endYear = endDateValue.getFullYear()
	const sameYear = startYear === endYear

	const ensureTrailingPeriod = (value: string) => (value.endsWith('.') ? value : `${value}.`)
	const startMonthLabelDesktop = hasRange ? ensureTrailingPeriod(startMonthShort) : startMonthFull
	const endMonthLabelDesktop = ensureTrailingPeriod(endMonthShort)

	let mobileDateLabel = `${startDayNumber}. ${startMonthFull}`
	if (hasRange) {
		mobileDateLabel =
			startMonthFull === endMonthFull
				? `${startDayNumber}.–${endDayNumber}. ${startMonthFull}`
				: `${startDayNumber}. ${startMonthFull}–${endDayNumber}. ${endMonthFull}`
	}

	return (
		<div className='container'>
			<div className='row'>
				<div className='col-12 col-lg-10 offset-lg-1 col-xl-8 offset-xl-2'>
					<div className='event-date'>
						<div className='event-date__card-date' aria-label='Dato for arrangement'>
							<div className='event-date__card-day-group'>
								<div className='event-date__card-day-group-inner'>
									<div className='event-date__card-day-start'>
										<span className='event-date__card-day'>{startDay}</span>
										<span className='event-date__card-month'>{startMonthLabelDesktop}</span>
										{(!hasRange || !sameYear) && (
											<span className='event-date__card-year'>{startYear}</span>
										)}
									</div>
									{hasRange && (
										<>
											<span className='event-date__card-separator'></span>
											<div className='event-date__card-day-end'>
												<span className='event-date__card-day'>{endDay}</span>
												<span className='event-date__card-month'>{endMonthLabelDesktop}</span>
												<span className='event-date__card-year'>{endYear}</span>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
						<div className='event-date__card-body'>
							<div className='event-date__card-meta'>
								{isNonWhitespaceString(time) && (
									<span className='event-date__meta-item'>{time}</span>
								)}
								{isNonWhitespaceString(location) && (
									<span className='event-date__meta-item event-date__meta-item--location'>
										<svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M11.8606 2.42418C14.4856 5.00585 14.4856 9.13752 11.8956 11.685L7.16977 16.3334L2.44227 11.685C1.82745 11.0843 1.33891 10.3667 1.00536 9.57445C0.671821 8.7822 0.5 7.93128 0.5 7.07168C0.5 6.21209 0.671821 5.36117 1.00536 4.56892C1.33891 3.77668 1.82745 3.0591 2.44227 2.45835L2.47727 2.42418C3.7274 1.19124 5.41267 0.5 7.16852 0.5C8.92437 0.5 10.6105 1.19124 11.8606 2.42418ZM11.8606 2.42418C11.8256 2.38918 11.8256 2.38918 11.8606 2.42418ZM8.83393 6.33335C8.83393 6.77538 8.65834 7.1993 8.34578 7.51186C8.03322 7.82442 7.6093 8.00002 7.16727 8.00002C6.72524 8.00002 6.30132 7.82442 5.98876 7.51186C5.6762 7.1993 5.5006 6.77538 5.5006 6.33335C5.5006 5.89132 5.6762 5.4674 5.98876 5.15484C6.30132 4.84228 6.72524 4.66668 7.16727 4.66668C7.6093 4.66668 8.03322 4.84228 8.34578 5.15484C8.65834 5.4674 8.83393 5.89132 8.83393 6.33335Z" stroke="#F9F6F1" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
										{location}
									</span>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

	)
}

